import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Minus, Plus } from 'lucide-react';
import BottomSheet from 'components/_UI/BottomSheet';
import colors from 'theme/colors';
import { panelAccent } from 'theme/panelAccent';

function formatPrice(value) {
    return Number(value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

export default function TicketDetailSheet({ isOpen, onClose, ticket, onAdd }) {
    const [quantity, setQuantity] = useState(1);

    // Reset quantity when sheet opens or ticket changes
    useEffect(() => {
        if (isOpen) setQuantity(1);
    }, [isOpen, ticket?.id]);

    if (!ticket) return null;

    const maxQty = ticket.available ?? 99;
    const isSoldOut = ticket.available === 0;
    const totalPrice = Number(ticket.base_price || 0) * quantity;

    function handleAdd() {
        if (!onAdd) return;
        onAdd(quantity);
        onClose();
    }

    return (
        <BottomSheet
            isOpen={isOpen}
            onClose={onClose}
            title="Adicionar ingresso"
            size="small"
        >
            <Content>
                <BatchRow>
                    <BatchName>{ticket.name}</BatchName>
                    <BatchPrice>{formatPrice(ticket.base_price)}</BatchPrice>
                </BatchRow>
                {ticket.available != null && (
                    <AvailableCount $soldOut={isSoldOut}>
                        {isSoldOut ? 'Esgotado' : `${ticket.available} ${ticket.available !== 1 ? 'disponíveis' : 'disponível'}`}
                    </AvailableCount>
                )}

                <QtyRow>
                    <QtyBtn
                        onClick={() => setQuantity(q => Math.max(1, q - 1))}
                        disabled={quantity <= 1}
                        aria-label="Diminuir quantidade"
                    >
                        <Minus size={18} color={quantity <= 1 ? colors.nightLight : colors.nightDark} />
                    </QtyBtn>
                    <QtyValue>{quantity}</QtyValue>
                    <QtyBtn
                        onClick={() => setQuantity(q => Math.min(maxQty, q + 1))}
                        disabled={quantity >= maxQty}
                        aria-label="Aumentar quantidade"
                    >
                        <Plus size={18} color={quantity >= maxQty ? colors.nightLight : colors.nightDark} />
                    </QtyBtn>
                </QtyRow>

                <AddBtn onClick={handleAdd} disabled={isSoldOut}>
                    Adicionar · {formatPrice(totalPrice)}
                </AddBtn>
            </Content>
        </BottomSheet>
    );
}

const Content = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 4px 0 8px;
`;

const BatchRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const BatchName = styled.span`
    font-size: 15px;
    font-weight: 600;
    color: ${colors.night};
`;

const BatchPrice = styled.span`
    font-size: 15px;
    font-weight: 600;
    color: ${panelAccent(colors.occasionalPurple)};
`;

const AvailableCount = styled.span`
    font-size: 12px;
    color: ${({ $soldOut }) => $soldOut ? colors.danger : colors.nightLight};
    margin-bottom: 8px;
`;

const QtyRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 24px;
    padding: 12px 0;
`;

const QtyBtn = styled.button`
    width: 40px;
    height: 40px;
    border-radius: 50%;
    border: 1.5px solid ${colors.neutralDark};
    background: transparent;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: ${({ disabled }) => disabled ? 'not-allowed' : 'pointer'};
    opacity: ${({ disabled }) => disabled ? 0.4 : 1};
    transition: background 0.15s;
    &:hover:not(:disabled) { background: ${colors.neutralDark}; }
`;

const QtyValue = styled.span`
    font-size: 24px;
    font-weight: 600;
    color: ${colors.nightDark};
    min-width: 32px;
    text-align: center;
`;

const AddBtn = styled.button`
    width: 100%;
    padding: 15px;
    border-radius: 12px;
    background: ${panelAccent(colors.occasionalPurple)};
    color: #fff;
    font-size: 16px;
    font-weight: 600;
    border: none;
    cursor: pointer;
    margin-top: 8px;
    transition: opacity 0.15s;
    &:hover:not(:disabled) { opacity: 0.88; }
    &:disabled { opacity: 0.4; cursor: not-allowed; }
`;
